export const FIREBASE_PASSWORD_PROVIDER_ID = "password";
export const FIREBASE_CREDENTIAL_EMAIL_MISMATCH_MESSAGE =
  "Firebase credential email does not match the account";

export interface FirebaseCredentialUser {
  uid: string;
  email?: string | null;
  isAnonymous?: boolean;
  providerData?: ReadonlyArray<{
    providerId?: string | null;
    email?: string | null;
  }>;
}

const normalizeEmail = (email?: string | null) =>
  email?.trim().toLowerCase() || null;

export const hasPasswordProvider = (
  user: FirebaseCredentialUser | null | undefined
) =>
  !!user?.providerData?.some(
    (provider) => provider.providerId === FIREBASE_PASSWORD_PROVIDER_ID
  );

export const hasVerifiedPasswordCredential = (
  user: FirebaseCredentialUser | null | undefined,
  expectedEmail?: string | null,
  expectedUid?: string | null
) => {
  if (!user || user.isAnonymous) return false;
  if (expectedUid && user.uid !== expectedUid) return false;

  const passwordProvider = user.providerData?.find(
    (provider) => provider.providerId === FIREBASE_PASSWORD_PROVIDER_ID
  );
  if (!passwordProvider) return false;

  const userEmail = normalizeEmail(user.email);
  const providerEmail = normalizeEmail(passwordProvider.email);
  const normalizedExpectedEmail = normalizeEmail(expectedEmail);

  if (!userEmail || !providerEmail) return false;
  if (userEmail !== providerEmail) return false;

  return !normalizedExpectedEmail || userEmail === normalizedExpectedEmail;
};

export const assertVerifiedPasswordCredential = (
  user: FirebaseCredentialUser | null | undefined,
  expectedEmail?: string | null,
  expectedUid?: string | null
) => {
  if (hasVerifiedPasswordCredential(user, expectedEmail, expectedUid)) return;

  if (!user) {
    throw new Error("Firebase credential link returned no user");
  }
  if (expectedUid && user.uid !== expectedUid) {
    throw new Error("Firebase credential linked to the wrong user");
  }
  if (user.isAnonymous) {
    throw new Error("Firebase user is still anonymous after credential link");
  }
  if (!hasPasswordProvider(user)) {
    throw new Error("Firebase password provider was not linked");
  }

  throw new Error(FIREBASE_CREDENTIAL_EMAIL_MISMATCH_MESSAGE);
};
